import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  LayoutDashboard, FileText, FolderKanban, ShoppingBag, GraduationCap,
  Briefcase, Users, Image, MessageSquare, Bell, Settings, Menu, X,
  LogOut, ChevronDown, Handshake, Calendar, ClipboardList, Database
} from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_d8692872-ce9e-4544-878c-3a5b2f1a67b0/artifacts/lff9980v_GTIC%20STUDIO%20LOGO2.png";

export default function AdminLayout() {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState({});
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchNotifications();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/admin/notifications?unread_only=true`);
      setNotifications(response.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { path: '/admin', icon: LayoutDashboard, label: t('admin_dashboard'), exact: true },
    { path: '/admin/services', icon: FileText, label: t('admin_services') },
    { path: '/admin/projects', icon: FolderKanban, label: t('admin_projects') },
    { path: '/admin/products', icon: ShoppingBag, label: t('admin_products') },
    { path: '/admin/trainings', icon: GraduationCap, label: t('admin_trainings') },
    { path: '/admin/jobs', icon: Briefcase, label: t('admin_jobs') },
    { path: '/admin/partners', icon: Handshake, label: t('admin_partners') },
    { path: '/admin/daily-life', icon: Calendar, label: t('admin_daily_life') },
    { path: '/admin/gallery', icon: Image, label: t('admin_gallery') },
    { 
      label: 'Demandes', 
      icon: ClipboardList,
      submenu: [
        { path: '/admin/contacts', label: t('admin_contacts'), badge: stats.unread_contacts },
        { path: '/admin/job-applications', label: t('admin_applications'), badge: stats.pending_applications },
        { path: '/admin/training-registrations', label: t('admin_registrations'), badge: stats.pending_registrations },
        { path: '/admin/partnership-requests', label: t('admin_partnership_requests'), badge: stats.pending_partnerships },
        { path: '/admin/quote-requests', label: t('admin_quote_requests'), badge: stats.pending_quotes },
      ]
    },
    { path: '/admin/users', icon: Users, label: t('admin_users'), adminOnly: true },
    { path: '/admin/backup', icon: Database, label: t('admin_backup') || 'Sauvegarde', adminOnly: true },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="admin-layout">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0F172A] transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-white/10">
            <Link to="/admin" className="flex items-center">
              <img src={LOGO_URL} alt="GTIC" className="h-8 brightness-0 invert" />
            </Link>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-white">
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {menuItems.map((item, index) => {
                if (item.adminOnly && user?.role !== 'admin') return null;
                
                if (item.submenu) {
                  return (
                    <div key={index} className="space-y-1">
                      <div className="flex items-center px-3 py-2 text-gray-400 text-sm font-medium">
                        <item.icon className="h-5 w-5 mr-3" />
                        {item.label}
                      </div>
                      <div className="ml-8 space-y-1">
                        {item.submenu.map((sub) => (
                          <Link
                            key={sub.path}
                            to={sub.path}
                            className={`flex items-center justify-between px-3 py-2 rounded-md text-sm ${
                              isActive(sub.path)
                                ? 'bg-[#0B6AB3] text-white'
                                : 'text-gray-400 hover:bg-white/10 hover:text-white'
                            }`}
                            onClick={() => setSidebarOpen(false)}
                          >
                            <span>{sub.label}</span>
                            {sub.badge > 0 && (
                              <Badge className="bg-[#E50E24] text-white text-xs">{sub.badge}</Badge>
                            )}
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                      isActive(item.path, item.exact)
                        ? 'bg-[#0B6AB3] text-white'
                        : 'text-gray-400 hover:bg-white/10 hover:text-white'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`admin-nav-${item.path.split('/').pop() || 'dashboard'}`}
                  >
                    <item.icon className="h-5 w-5 mr-3" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>

          {/* User Info */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0B6AB3] rounded-full flex items-center justify-center text-white font-medium">
                {user?.name?.charAt(0) || 'A'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{user?.name}</p>
                <p className="text-gray-400 text-xs truncate">{user?.email}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-40 bg-white shadow-sm h-16 flex items-center px-4 lg:px-6">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 -ml-2 text-gray-600"
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex-1" />

          <div className="flex items-center gap-4">
            {/* Notifications */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="relative" data-testid="notifications-btn">
                  <Bell className="h-5 w-5" />
                  {stats.unread_notifications > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#E50E24] text-white text-xs rounded-full flex items-center justify-center">
                      {stats.unread_notifications}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <div className="p-2 border-b">
                  <p className="font-semibold">Notifications</p>
                </div>
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    Aucune nouvelle notification
                  </div>
                ) : (
                  notifications.map((notif) => (
                    <DropdownMenuItem key={notif.id} className="flex flex-col items-start gap-1 p-3">
                      <span className="font-medium text-sm">{notif.title}</span>
                      <span className="text-xs text-gray-500">{notif.message}</span>
                    </DropdownMenuItem>
                  ))
                )}
                <div className="p-2 border-t">
                  <Link to="/admin/contacts" className="text-[#0B6AB3] text-sm hover:underline">
                    Voir tout
                  </Link>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* View Site */}
            <Link to="/" target="_blank">
              <Button variant="outline" size="sm" data-testid="view-site-btn">
                Voir le site
              </Button>
            </Link>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-btn">
                  <div className="w-8 h-8 bg-[#0B6AB3] rounded-full flex items-center justify-center text-white text-sm font-medium">
                    {user?.name?.charAt(0) || 'A'}
                  </div>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                  <LogOut className="h-4 w-4 mr-2" />
                  {t('nav_logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          <Outlet context={{ stats, refreshStats: fetchStats }} />
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
